import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListSubheader from '@material-ui/core/ListSubheader';
import * as yup from 'yup';
import { DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment from 'moment-timezone';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Switch from '@material-ui/core/Switch';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import { useFormik } from 'formik';
import TextField from '@material-ui/core/TextField';
import { useDispatch, useSelector } from 'react-redux';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import FilledInput from '@material-ui/core/FilledInput';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import mixpanel from 'mixpanel-browser';

import * as constants from '../../../constants';
import { add } from '../../../store/features/postOrders';
import PropertiesSearchDialog from '../PropertiesSearch';

const initTime = (timezone) => ({
  start: moment().tz(timezone),
  end: moment().tz(timezone)
});    

const AddPostOrder = ({ isOpen, onClose, timezone }) => {
  const [time, setTime] = useState(() => initTime(timezone));
  const [dar, setDar] = useState({ isRequired: false });
  const [timeWindow, setTimeWindow] = useState(false);
  const [showPropertiesSearch, setShowPropertiesSearch] = useState(false);
  const [property, setProperty] = useState({ displayName: '' });
  const [propertyId, setPropertyId] = useState(null);

  const dispatch = useDispatch();
  const propertiesById = useSelector(state => state.properties.byId);
  

  useEffect(() => {
    if(propertyId) setProperty(propertiesById[propertyId]);
    else {
      setProperty({ displayName: ''});
      setDar({ isRequired: false });
    }
  }, [propertyId]);

  useEffect(() => {
    if(isOpen) {
      formData.handleReset();
      setTime(() => initTime(timezone));
      setDar({ isRequired: false });
      setTimeWindow(false);
      setShowPropertiesSearch(false);  
      setProperty({ displayName: '' });
      setPropertyId(null);
    }
  }, [isOpen]);

  const formData = useFormik({
    initialValues: { 
      description: '',
    },
    onSubmit: values => formSubmit(values),
    validationSchema: yup.object({
      description: yup.string()
      .max(140, 'Must be 140 characters or less')
      .required('Required')
    })
  });

  const formSubmit = async values => {
    const postOrder = {
      description: values.description,
      dar
    };
    
    if(timeWindow) {
      postOrder.time = { 
        start: time.start.valueOf(), 
        end: time.end.valueOf()
      }
    }
    if(propertyId) {
      postOrder.propertyId = propertyId;
    }

    onClose();
    dispatch(add({ postOrder }));
    mixpanel.track(
      constants.mixpanel.SCHEDULING_ADD_POST_ORDER, 
      {
        propertyId: Boolean(propertyId),
        timeWindow: Boolean(timeWindow),
        dar: Boolean(dar.isRequired)
      }
    );
  };

  const descriptionError = formData.touched.description && formData.errors.description;

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        fullWidth
        maxWidth='md'
      >
        <DialogTitle>Post order</DialogTitle>
        <DialogContent>
        <List>
          <ListSubheader disableSticky>General</ListSubheader>
          <ListItem>
            <TextField
              fullWidth 
              required
              id={'description'}
              variant={'filled'}
              onBlur={formData.onBlur}
              value={formData.values.description}
              onChange={formData.handleChange}
              error={descriptionError}
              helperText={descriptionError ? formData.errors.description : null}
              label={'Description'}
            />
          </ListItem>
          <ListSubheader disableSticky>Conditions</ListSubheader>
            <ListItem>
              <FormControl variant={'filled'} fullWidth>
                <InputLabel htmlFor='location'>Location</InputLabel>
                <FilledInput
                  id={'location'}
                  fullWidth
                  variant={'filled'}
                  InputProps={{
                    readOnly: true
                  }}
                  onClick={() => setShowPropertiesSearch(true)}
                  value={property.displayName}
                  label={'Name'}
                  endAdornment={
                    Boolean(propertyId) && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setPropertyId(null);
                          }}
                          edge="end"
                        >
                          <CloseIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                />
              </FormControl>
            </ListItem>
            {
              Boolean(propertyId) &&
              <ListItem>
                <ListItemIcon>
                  <Checkbox
                    checked={dar.isRequired}
                    onClick={() => setDar({ ...dar, isRequired: !dar.isRequired})}
                    edge='start'
                  />
                </ListItemIcon>
                <ListItemText secondary={'Require daily activity report'} />
              </ListItem>
            }
          <ListItem>
            <ListItemIcon>
              <Switch edge='start' checked={timeWindow} onChange={() => setTimeWindow(!timeWindow)} />
            </ListItemIcon>
            <ListItemText>
              Use a time window
            </ListItemText>
          </ListItem>
          {
            timeWindow && (
              <ListItem>
                <Grid container spacing={2}>
                  <MuiPickersUtilsProvider utils={MomentUtils} >
                    <Grid item xs={6}>                
                      <DateTimePicker
                        ampm={false}
                        format={constants.moment.format}
                        label='Starts At'
                        inputVariant='filled'
                        value={time.start}
                        onChange={start => {
                          if (!start) return;
                          setTime({...time, start});
                        }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <DateTimePicker
                        ampm={false}
                        format={constants.moment.format}
                        label='Ends At'
                        inputVariant='filled'
                        value={time.end}
                        onChange={end => {
                          if (!end) return;
                          setTime({...time, end});
                        }}
                        fullWidth
                      />
                    </Grid>
                  </MuiPickersUtilsProvider>
                </Grid>
              </ListItem>
            )
          }
        </List>
        </DialogContent>
        <DialogActions>
          <Button type='submit' color='primary' onClick={formData.handleSubmit}>
            Save
          </Button>
          <Button onClick={onClose} color='primary' autoFocus>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <PropertiesSearchDialog 
        open={showPropertiesSearch}
        onClose={() => setShowPropertiesSearch(false)}
        onPropertyClick={id => setPropertyId(id)}
      />
    </>
  );
};

export default AddPostOrder;